import {
  Box, Typography, Chip, Stack, Avatar,
} from '@mui/material';
import {
  ErrorOutline, WarningAmber, ArrowForwardIos, Person, AccessTime,
} from '@mui/icons-material';
import type { TaskDueAlert, PayrollAlert } from '@smartgrow/shared';

const daysUntil = (dateStr: string) => {
  const diff = new Date(dateStr).getTime() - Date.now();
  return Math.ceil(diff / 86400000);
};

const fmtDate = (dateStr: string) =>
  new Date(dateStr).toLocaleDateString('en-NG', { day: 'numeric', month: 'short', year: 'numeric' });

interface TaskAlertCardProps {
  alert: TaskDueAlert;
  onClick?: () => void;
}

export const TaskAlertCard = ({ alert, onClick }: TaskAlertCardProps) => {
  const isDanger = alert.severity === 'DANGER';
  const days = daysUntil(alert.scheduledDate);
  const accentColor = isDanger ? '#D32F2F' : '#E65100';
  const bgColor = isDanger ? '#FFF5F5' : '#FFFBF0';
  const chipColor = isDanger ? 'error' : 'warning';

  return (
    <Box
      onClick={onClick}
      sx={{
        display: 'flex',
        alignItems: 'stretch',
        borderRadius: 2,
        overflow: 'hidden',
        border: '1px solid',
        borderColor: isDanger ? 'error.light' : 'warning.light',
        bgcolor: bgColor,
        cursor: onClick ? 'pointer' : 'default',
        transition: 'box-shadow 0.15s, transform 0.15s',
        '&:hover': onClick ? {
          boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
          transform: 'translateY(-1px)',
        } : {},
      }}
    >
      {/* Left accent bar */}
      <Box sx={{ width: 5, bgcolor: accentColor, flexShrink: 0 }} />

      {/* Icon */}
      <Box sx={{ display: 'flex', alignItems: 'center', px: 1.5 }}>
        <Avatar sx={{ bgcolor: isDanger ? 'error.light' : 'warning.light', width: 36, height: 36 }}>
          {isDanger
            ? <ErrorOutline sx={{ color: 'error.main', fontSize: 20 }} />
            : <WarningAmber sx={{ color: 'warning.main', fontSize: 20 }} />}
        </Avatar>
      </Box>

      {/* Content */}
      <Box sx={{ flex: 1, py: 1.5, pr: 1 }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
          <Typography variant="body2" fontWeight={700} sx={{ color: 'text.primary' }}>
            {alert.title}
          </Typography>
          <Chip
            label={alert.taskType.replace(/_/g, ' ')}
            size="small"
            color={chipColor}
            variant="outlined"
            sx={{ height: 18, fontSize: '0.6rem', fontWeight: 600 }}
          />
        </Stack>

        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          {alert.message}
        </Typography>

        <Stack direction="row" spacing={1.5} alignItems="center">
          <Stack direction="row" spacing={0.5} alignItems="center">
            <AccessTime sx={{ fontSize: 13, color: accentColor }} />
            <Typography variant="caption" sx={{ color: accentColor, fontWeight: 600 }}>
              {days < 0 ? `${Math.abs(days)}d overdue` : days === 0 ? 'Due today' : `${days}d left`}
            </Typography>
          </Stack>
          <Typography variant="caption" color="text.disabled">
            {fmtDate(alert.scheduledDate)}
          </Typography>
        </Stack>
      </Box>

      {/* Arrow */}
      {onClick && (
        <Box sx={{ display: 'flex', alignItems: 'center', pr: 1.5 }}>
          <ArrowForwardIos sx={{ fontSize: 14, color: 'text.disabled' }} />
        </Box>
      )}
    </Box>
  );
};

interface PayrollAlertCardProps {
  alert: PayrollAlert;
  onClick?: () => void;
}

export const PayrollAlertCard = ({ alert, onClick }: PayrollAlertCardProps) => {
  const isDanger = alert.severity === 'DANGER';
  const accentColor = isDanger ? '#D32F2F' : '#E65100';
  const bgColor = isDanger ? '#FFF5F5' : '#FFFBF0';

  return (
    <Box
      onClick={onClick}
      sx={{
        display: 'flex',
        alignItems: 'stretch',
        borderRadius: 2,
        overflow: 'hidden',
        border: '1px solid',
        borderColor: isDanger ? 'error.light' : 'warning.light',
        bgcolor: bgColor,
        cursor: onClick ? 'pointer' : 'default',
        transition: 'box-shadow 0.15s, transform 0.15s',
        '&:hover': onClick ? {
          boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
          transform: 'translateY(-1px)',
        } : {},
      }}
    >
      {/* Left accent bar */}
      <Box sx={{ width: 5, bgcolor: accentColor, flexShrink: 0 }} />

      {/* Icon */}
      <Box sx={{ display: 'flex', alignItems: 'center', px: 1.5 }}>
        <Avatar sx={{ bgcolor: isDanger ? 'error.light' : 'warning.light', width: 36, height: 36 }}>
          <Person sx={{ color: isDanger ? 'error.main' : 'warning.main', fontSize: 20 }} />
        </Avatar>
      </Box>

      {/* Content */}
      <Box sx={{ flex: 1, py: 1.5, pr: 1 }}>
        <Typography variant="body2" fontWeight={700} sx={{ color: 'text.primary', mb: 0.25 }}>
          {alert.title}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
          {alert.message}
        </Typography>
        <Typography variant="caption" sx={{ color: accentColor, fontWeight: 700 }}>
          ₦{alert.monthlySalary.toLocaleString()}
        </Typography>
      </Box>

      {/* Severity badge */}
      <Box sx={{ display: 'flex', alignItems: 'center', pr: 1.5 }}>
        <Chip
          label={isDanger ? 'OVERDUE' : 'DUE SOON'}
          size="small"
          color={isDanger ? 'error' : 'warning'}
          sx={{ height: 20, fontSize: '0.6rem', fontWeight: 700 }}
        />
      </Box>
    </Box>
  );
};
